/**
 * HOS Route Migration & Consolidation
 * 
 * This module handles the migration from legacy routes (/admin, /staff-portal, /hospice-ehr)
 * to the unified /hos environment. It provides:
 * 
 * 1. Route redirects from old paths to new /hos paths
 * 2. Unified navigation structure
 * 3. Breadcrumb generation for /hos paths
 * 4. Feature mapping for consolidated access
 */

import { Navigate, useLocation, useParams } from "react-router-dom";

// Legacy route to HOS route mapping
export const ROUTE_MIGRATION_MAP = {
  // Admin routes -> /hos
  "/admin": "/hos",
  "/admin/activities": "/hos?view=activities",
  "/admin/triage": "/hos?view=triage",
  "/admin/ehr": "/hos?view=ehr-dashboard",
  "/admin/ai-features": "/hos?view=ai-features",
  "/admin/clinical-intelligence": "/hos?view=clinical-intelligence",
  "/admin/analytics": "/hos?view=analytics",
  "/admin/scheduling": "/hos?view=scheduling",
  "/admin/realtime": "/hos?view=realtime-ops",
  "/admin/predictive": "/hos?view=predictive-analytics",
  "/admin/quality": "/hos?view=quality-metrics",
  "/admin/hipaa": "/hos?view=hipaa",
  "/admin/video-analytics": "/hos?view=video-analytics",
  
  // Staff portal routes -> /hos
  "/staff-portal": "/hos",
  "/staff-roster": "/hos?view=staff-roster",
  "/staff-tools": "/hos?view=staff-tools",
  
  // Hospice EHR routes -> /hos
  "/hospice-ehr": "/hos",
  "/hospice-ehr/voice-documentation": "/hos?view=voice-documentation",
  "/hospice-ehr/clinical-intelligence": "/hos?view=clinical-intelligence",
  "/hospice-ehr/command-center": "/hos?view=command-center",
  "/hospice-ehr/documents": "/hos?view=documents",
  "/hospice-ehr/referral-to-emr": "/hos?view=referral-to-emr",
  "/hospice-ehr/compliance": "/hos?view=compliance",
  "/hospice-ehr/medications": "/hos?view=medications",
  "/hospice-ehr/analytics": "/hos?view=analytics",
  "/hospice-ehr/transitions": "/hos?view=transitions",
  "/hospice-ehr/volunteer": "/hos?view=volunteer",
  "/hospice-ehr/scheduling": "/hos?view=scheduling",
  "/hospice-ehr/interoperability": "/hos?view=interoperability",
  "/hospice-ehr/regulatory": "/hos?view=regulatory",
  "/hospice-ehr/rcm": "/hos?view=rcm",
  "/hospice-ehr/operations": "/hos?view=operations",
  "/hospice-ehr/claims": "/hos?view=claims",
  "/hospice-ehr/peer-review": "/hos?view=peer-review",
  "/hospice-ehr/financial-reports": "/hos?view=financial-reports",
  "/hospice-ehr/comprehensive-reports": "/hos?view=reports",
  
  // EHR feature routes -> /hos
  "/ehr-compliance": "/hos?view=compliance",
  "/ehr-referral-intake": "/hos?view=referral-intake",
  "/ehr-orders-medications": "/hos?view=medications",
  "/ehr-idg-meeting": "/hos?view=idg-meeting",
  "/ehr-discharge-revocation": "/hos?view=discharge",
  "/ehr-staff-visits": "/hos?view=visits",
  "/ehr-ai-care-plans": "/hos?view=care-plans",
  "/ehr-documentation-qa": "/hos?view=documentation-qa",
  "/ehr-cti-grader": "/hos?view=cti-grader",
  "/ehr-ai-scheduling": "/hos?view=ai-scheduling",
  "/ehr-oms-automation": "/hos?view=oms-automation",
  "/ehr-fall-automation": "/hos?view=fall-automation",
  "/ehr-qapi-falls": "/hos?view=qapi-falls",
  "/ehr-predictive-falls": "/hos?view=predictive-falls",
  "/ehr-skin-automation": "/hos?view=skin-automation",
  "/ehr-respiratory-crisis": "/hos?view=respiratory-crisis",
  
  // Other hospice-related routes
  "/hospice-eligibility": "/hos?view=eligibility",
  "/care-calendar": "/hos?view=calendar",
  "/command-center": "/hos?view=command-center",
  "/patient-outcomes": "/hos?view=outcomes"
};

// HOS Navigation structure
export const HOS_NAVIGATION = {
  main: [
    {
      id: "dashboard",
      label: "Dashboard",
      path: "/hos",
      icon: "LayoutDashboard",
      description: "Main HOS dashboard with patient context"
    },
    {
      id: "patients",
      label: "Patients",
      path: "/hos?view=patients",
      icon: "Users",
      description: "Patient management and records"
    },
    {
      id: "clinical",
      label: "Clinical",
      path: "/hos?view=clinical",
      icon: "Stethoscope",
      children: [
        { id: "hope", label: "HOPE Assessments", path: "/hos?view=hope", shortcut: "Ctrl+H" },
        { id: "templates", label: "Clinical Templates", path: "/hos?view=templates", shortcut: "Ctrl+T" },
        { id: "visits", label: "Visit Notes", path: "/hos?view=visits" },
        { id: "medications", label: "Medications", path: "/hos?view=medications" },
        { id: "care-plans", label: "Care Plans", path: "/hos?view=care-plans" },
        { id: "pain-management", label: "Pain Management", path: "/hos?view=pain" },
        { id: "wound-care", label: "Wound Care", path: "/hos?view=wound" }
      ]
    },
    {
      id: "compliance",
      label: "Compliance",
      path: "/hos?view=compliance",
      icon: "Shield",
      children: [
        { id: "hope-compliance", label: "HOPE Compliance", path: "/hos?view=hope-compliance" },
        { id: "iqies", label: "iQIES Export", path: "/hos?view=iqies" },
        { id: "regulatory", label: "Regulatory", path: "/hos?view=regulatory" },
        { id: "hipaa", label: "HIPAA", path: "/hos?view=hipaa" },
        { id: "qapi", label: "QAPI", path: "/hos?view=qapi" }
      ]
    },
    {
      id: "scheduling",
      label: "Scheduling",
      path: "/hos?view=scheduling",
      icon: "Calendar",
      children: [
        { id: "calendar", label: "Care Calendar", path: "/hos?view=calendar" },
        { id: "visits", label: "Visit Scheduling", path: "/hos?view=visit-scheduling" },
        { id: "idg", label: "IDG Meetings", path: "/hos?view=idg" }
      ]
    },
    {
      id: "communication",
      label: "Communication",
      path: "/hos?view=communication",
      icon: "MessageSquare",
      children: [
        { id: "family", label: "Family Portal", path: "/hos?view=family" },
        { id: "team-chat", label: "Team Chat", path: "/hos?view=chat" },
        { id: "notifications", label: "Notifications", path: "/hos?view=notifications" }
      ]
    },
    {
      id: "analytics",
      label: "Analytics",
      path: "/hos?view=analytics",
      icon: "BarChart3",
      children: [
        { id: "operations", label: "Operations", path: "/hos?view=operations" },
        { id: "outcomes", label: "Patient Outcomes", path: "/hos?view=outcomes" },
        { id: "predictive", label: "Predictive Analytics", path: "/hos?view=predictive" },
        { id: "financial", label: "Financial Reports", path: "/hos?view=financial" }
      ]
    }
  ]
};

// Breadcrumb configuration for HOS paths
export const HOS_BREADCRUMBS = {
  "/hos": [{ label: "Home", path: "/" }, { label: "Hospice OS", path: "/hos" }],
  "/hos?view=hope": [{ label: "Home", path: "/" }, { label: "Hospice OS", path: "/hos" }, { label: "HOPE Assessments", path: "/hos?view=hope" }],
  "/hos?view=templates": [{ label: "Home", path: "/" }, { label: "Hospice OS", path: "/hos" }, { label: "Clinical Templates", path: "/hos?view=templates" }],
  "/hos?view=medications": [{ label: "Home", path: "/" }, { label: "Hospice OS", path: "/hos" }, { label: "Medications", path: "/hos?view=medications" }],
  "/hos?view=compliance": [{ label: "Home", path: "/" }, { label: "Hospice OS", path: "/hos" }, { label: "Compliance", path: "/hos?view=compliance" }],
  "/hos?view=iqies": [{ label: "Home", path: "/" }, { label: "Hospice OS", path: "/hos" }, { label: "iQIES Export", path: "/hos?view=iqies" }],
  "/hos?view=scheduling": [{ label: "Home", path: "/" }, { label: "Hospice OS", path: "/hos" }, { label: "Scheduling", path: "/hos?view=scheduling" }],
  "/hos?view=analytics": [{ label: "Home", path: "/" }, { label: "Hospice OS", path: "/hos" }, { label: "Analytics", path: "/hos?view=analytics" }]
};

// Generate breadcrumbs from current path
export function generateBreadcrumbs(pathname, search = "") {
  const fullPath = pathname + search;
  
  // Check if we have a predefined breadcrumb
  if (HOS_BREADCRUMBS[fullPath]) {
    return HOS_BREADCRUMBS[fullPath];
  }
  
  // Generate dynamic breadcrumbs for /hos paths
  if (pathname.startsWith("/hos")) {
    const breadcrumbs = [
      { label: "Home", path: "/" },
      { label: "Hospice OS", path: "/hos" }
    ];
    
    // Parse view parameter
    const params = new URLSearchParams(search);
    const view = params.get("view");
    
    if (view) {
      // Convert view parameter to label
      const label = view
        .split("-")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      
      breadcrumbs.push({ label, path: fullPath });
    }
    
    return breadcrumbs;
  }
  
  // Default breadcrumbs for other paths
  return [{ label: "Home", path: "/" }];
}

// Route redirect component for legacy paths
export function LegacyRouteRedirect() {
  const location = useLocation();
  const currentPath = location.pathname;
  
  // Check if this is a legacy route that needs redirect
  const newPath = ROUTE_MIGRATION_MAP[currentPath];
  
  if (newPath) {
    // Log the redirect for monitoring
    console.log(`[HOS Migration] Redirecting from ${currentPath} to ${newPath}`);
    return <Navigate to={newPath} replace />;
  }
  
  // If no mapping found, return null (don't redirect)
  return null;
}

// HOSBreadcrumbs component for use in HOS pages
export function HOSBreadcrumbs({ customCrumbs = null }) {
  const location = useLocation();
  const breadcrumbs = customCrumbs || generateBreadcrumbs(location.pathname, location.search);
  
  return (
    <nav className="flex items-center space-x-2 text-sm text-slate-400 mb-4" aria-label="Breadcrumb">
      {breadcrumbs.map((crumb, index) => (
        <div key={crumb.path} className="flex items-center">
          {index > 0 && <span className="mx-2 text-slate-600">/</span>}
          {index === breadcrumbs.length - 1 ? (
            <span className="text-slate-200">{crumb.label}</span>
          ) : (
            <a 
              href={crumb.path} 
              className="hover:text-teal-400 transition-colors"
            >
              {crumb.label}
            </a>
          )}
        </div>
      ))}
    </nav>
  );
}

// Feature availability check for consolidated HOS
export function isFeatureAvailable(featureId) {
  const availableFeatures = [
    "hope", "templates", "iqies", "medications", "visits",
    "compliance", "scheduling", "calendar", "analytics",
    "family", "chat", "notifications", "patients"
  ];
  
  return availableFeatures.includes(featureId);
}

// Export all route mappings for sitemap generation
export function getAllHOSRoutes() {
  const routes = new Set();
  
  // Add main HOS route
  routes.add("/hos");
  
  // Add all navigation routes
  HOS_NAVIGATION.main.forEach(item => {
    if (item.path) routes.add(item.path);
    if (item.children) {
      item.children.forEach(child => {
        if (child.path) routes.add(child.path);
      });
    }
  });
  
  return Array.from(routes);
}

// For backwards compatibility - check if path should redirect
export function shouldRedirectToHOS(pathname) {
  return Object.keys(ROUTE_MIGRATION_MAP).includes(pathname);
}

export default {
  ROUTE_MIGRATION_MAP,
  HOS_NAVIGATION,
  HOS_BREADCRUMBS,
  generateBreadcrumbs,
  LegacyRouteRedirect,
  HOSBreadcrumbs,
  isFeatureAvailable,
  getAllHOSRoutes,
  shouldRedirectToHOS
};
